import { yupResolver } from '@hookform/resolvers/yup';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { MouseEventHandler, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { InferType, object, string } from 'yup';

import { postCurrencyLockApi, postCurrencyUnlockApi } from '@flipster/apis/v1/currency';
import { FormattedMessage } from '@flipster/i18n';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { Modal } from '@flipster/react/design-system/modal';
import { createSegmentedControl } from '@flipster/react/design-system/segmented-control';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import BoltIcon from '@flipster/react/icon/generated/BoltIcon';
import TrendIcon from '@flipster/react/icon/generated/TrendIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { DefaultNumberInputMaxLength, FormItem } from '@flipster/ui';
import { PusdtCurrency, UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getErrorMessage } from '@flipster/utils/error';
import Num, { BigNumber, formatToShortNumber, inputFormatter, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { UsdtPremiumEvent } from '@flipster/utils/segment/events/usdt-premium';

import { earnSummaryBunja, getEarnSummaryOfCurrency } from '../../../shared/earn/state';
import PriceRateButtons from './price-rate-buttons';
import SendPriceInput from './send-price-input';

type ModalSchema = InferType<typeof modalValidator>;
const modalValidator = object({
  amount: string().required(),
});

const SegmentedControl = createSegmentedControl<'subscribe' | 'redeem'>();

interface ManagePusdtModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const ManagePusdtModal = ({ isOpen, onClose, onExit }: ManagePusdtModalProps) => {
  const [tab, setTab] = useState<'subscribe' | 'redeem'>('subscribe');
  const { earnSummaryAtom, refetch: refetchEarn } = useBunja(earnSummaryBunja);
  const { walletsAtom } = useBunja(walletsBunja);

  const wallets = useAtomValue(walletsAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const { pendingReductions, maxCap } = getEarnSummaryOfCurrency(PusdtCurrency, earnSummary);
  const pendingReductionsSum =
    pendingReductions.length > 0 ? BigNumber.sum(...pendingReductions.map((r) => Num(r.amount))) : Num('0');

  const assetAmount =
    tab === 'subscribe'
      ? wallets?.[UsdtCurrency]?.walletBalanceWithdrawable
      : Num(wallets?.[PusdtCurrency]?.walletBalanceExclWithdrawalPending ?? '0').minus(pendingReductionsSum);

  const methods = useForm<ModalSchema>({
    mode: 'onTouched',
    resolver: yupResolver(modalValidator),
    defaultValues: {
      amount: '0',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
    watch,
  } = methods;

  const isDisabled = !isValid || Num(watch('amount')).isZero();

  const onSubmit = async (data: ModalSchema) => {
    try {
      if (tab === 'subscribe') {
        await postCurrencyLockApi(UsdtCurrency, { amount: data.amount });
      } else {
        await postCurrencyUnlockApi(UsdtCurrency, { amount: data.amount });
      }
      onClose();
      reset();
      refetchEarn();
      const formattedAmount = numberFormatter(data.amount, { precision: UsdtPrecision, toFixed: true });
      snackbar.success({
        message:
          tab === 'subscribe' ? (
            <FormattedMessage id='Earn.r260220.Pusdt_subscription_success_toast' values={{ amount: formattedAmount }} />
          ) : (
            <FormattedMessage id='Earn.r260220.Pusdt_redemption_success_toast' values={{ amount: formattedAmount }} />
          ),
      });
      segment.track(UsdtPremiumEvent.UsdtPremiumConfirmClick, {
        usdt: Num(data.amount).toNumber(),
        manage_type: tab === 'subscribe' ? 'Subscribe' : 'Redeem',
      });
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    }
  };

  const onClickPercent: MouseEventHandler<HTMLButtonElement> = (e) => {
    const { value } = e.currentTarget;
    if (!assetAmount) return;
    const calculatedAmount = Num(assetAmount)
      .dividedBy(100)
      .multipliedBy(value)
      .toFixed(UsdtPrecision, BigNumber.ROUND_DOWN);
    const formattedAmount = inputFormatter(calculatedAmount, {
      withComma: true,
      maxLength: DefaultNumberInputMaxLength,
    });
    const withoutComma = inputFormatter(formattedAmount, { withComma: false });
    setValue('amount', withoutComma, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Earn.r260220.Pusdt_manage_premium_earn' />} />
      </Modal.Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className={css(stack.raw({ flex: 1 }))}>
          <Modal.Content>
            <SegmentedControl
              value={tab}
              defaultValue='subscribe'
              size='medium'
              css={{ width: '100%' }}
              onChange={(value) => {
                setTab(value);
                reset(undefined, { keepDefaultValues: true });
              }}
            >
              <SegmentedControl.Item value='subscribe'>
                <div className={css({ textStyle: 'Text/S/Strong' })}>
                  <FormattedMessage id='Earn.r251204.Subscribe' />
                </div>
              </SegmentedControl.Item>
              <SegmentedControl.Item value='redeem'>
                <div className={css({ textStyle: 'Text/S/Strong' })}>
                  <FormattedMessage id='Redeem.r251008.Redeem' />
                </div>
              </SegmentedControl.Item>
            </SegmentedControl>
            <div
              className={flex({
                align: 'start',
                justify: 'center',
                textStyle: 'Title/M',
                color: 'content.default.level-5',
                p: 24,
              })}
            >
              <FormItem name='amount' error={errors.amount?.message} className={css({ textAlign: 'center' })}>
                <SendPriceInput name='amount' precision={UsdtPrecision} currency={UsdtCurrency} />
              </FormItem>
            </div>
            <div
              className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2', textAlign: 'center' })}
            >
              {tab === 'subscribe' ? (
                <div className={stack({ gap: 4 })}>
                  <FormattedMessage id='Earn.r260220.Pusdt_enter_subscription_amount_in_usdt' />
                  <div
                    className={hstack({
                      gap: 4,
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: 'content.default.level-5',
                      textStyle: 'Text/M/Regular',
                    })}
                  >
                    <TrendIcon size={16} color={token('colors.content.state.positive')} />
                    <FormattedMessage
                      id='Earn.r260408.Apr_applies_up_to_cap_usdt'
                      values={{ cap: formatToShortNumber(maxCap.toString()) }}
                    />
                  </div>
                </div>
              ) : (
                <div className={stack({ gap: 4 })}>
                  <FormattedMessage id='Earn.r260220.Pusdt_redeem_premium_earn_usdt_for_usdt' />
                  <div
                    className={hstack({
                      gap: 4,
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: 'content.default.level-5',
                      textStyle: 'Text/M/Regular',
                    })}
                  >
                    <BoltIcon size={16} color={token('colors.content.state.positive')} />
                    <FormattedMessage id='Earn.r260108.Instant_redemption' />
                  </div>
                </div>
              )}
            </div>
            <PriceRateButtons
              items={[
                { label: '25%', value: '25' },
                { label: '50%', value: '50' },
                { label: '75%', value: '75' },
                { label: '100%', value: '100' },
              ]}
              onClick={onClickPercent}
              className={css({ mt: 24 })}
            />
            <dl
              className={stack({
                mt: 32,
                p: '12px 16px',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.default.level-1',
                borderRadius: 8,
                gap: 4,
                '& > div': {
                  display: 'flex',
                  justifyContent: 'space-between',
                },
                '& dt': {
                  color: 'content.default.level-2',
                  textStyle: 'Text/S/Regular',
                },
                '& dd': {
                  color: 'content.default.level-5',
                  textStyle: 'Text/M/Regular',
                },
              })}
            >
              <div>
                <dt>
                  <FormattedMessage id='Autogen_common.r251126.s_Est_processing_time' />
                </dt>
                <dd>
                  {tab === 'subscribe' ? (
                    <FormattedMessage id='Earn.r260108.Instant' />
                  ) : (
                    <FormattedMessage id='Earn.r260318.Pusdt_redemption_processing_time' />
                  )}
                </dd>
              </div>
              <div>
                <dt>
                  <FormattedMessage id='Common.r251204.Fee' />
                </dt>
                <dd>
                  {/* 0.00% fixed */}
                  <Formatted.Percent value={0} precision={2} toFixed />
                </dd>
              </div>
            </dl>
          </Modal.Content>
          <Modal.Footer>
            <Modal.Button
              size='large'
              label={<FormattedMessage id='Autogen_common.r251126.s_Confirm' />}
              disabled={isDisabled}
              onClick={handleSubmit(onSubmit)}
            />
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal.Root>
  );
};
